import AgentIntegrationsForm from "@/components/agents/agent-integrations-form"
import AgentMetricsForm from "@/components/agents/agent-metrics-form"
import AgentPromptVersions from "@/components/agents/agent-prompt-versions"
import AgentResourcesForm from "@/components/agents/agent-resources-form"
import InputError from "@/components/input-error"
import { Checkbox } from "@/components/ui/checkbox"
import { CurrencyInput } from "@/components/ui/currency-input"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { MarkdownInput } from "@/components/ui/markdown-input"
import RecordFieldTooltip from "@/components/ui/record-field-tooltip"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Textarea } from "@/components/ui/text-area"
import type {
  Account,
  AgentMetric,
  AgentPromptVersion,
  AgentResource,
  AvailableModel,
  AvailableSkill,
} from "@/types"

interface AgentFormProps {
  data: {
    agent: {
      name: string
      description: string
      problem_solved: string
      summary: string
      state:
        | "provisional"
        | "draft"
        | "operational"
        | "degraded"
        | "paused"
        | "retired"
      account_uid: string
      hourly_rate?: number
      manual_task_time_in_minutes?: number
      external_id?: string
      slug?: string
      system_prompt?: string
      model_id?: string
      launch_title?: string
      launch_prompt?: string
      launch_icon?: string
      skills?: string[]
      agent_metrics_attributes?: {
        id?: number
        uid?: string
        label: string
        description: string
        data_source_identifier: string
        _destroy?: boolean
      }[]
      integration_kinds?: string[]
      resources_by_kind?: Record<string, AgentResource[]>
    }
  }
  accounts: Account[]
  availableSkills?: AvailableSkill[]
  availableModels?: AvailableModel[]
  existingMetrics?: AgentMetric[]
  promptVersions?: AgentPromptVersion[]
  setData: (key: string, value: any) => void
  errors: Record<string, any>
  mode?: "new" | "edit"
}

export default function AgentForm({
  data,
  setData,
  errors,
  accounts,
  availableSkills = [],
  availableModels = [],
  existingMetrics,
  promptVersions = [],
  mode = "new",
}: AgentFormProps) {
  const generalFields = (
    <>
      <div className="grid gap-2">
        <Label htmlFor="account_uid">Account</Label>

        <Select
          value={data.agent.account_uid}
          onValueChange={(val) =>
            setData("agent", { ...data.agent, account_uid: val })
          }
        >
          <SelectTrigger id="account_uid" className="w-full">
            <SelectValue placeholder="Select an account" />
          </SelectTrigger>
          <SelectContent>
            {accounts.map((account) => (
              <SelectItem key={account.uid} value={account.uid}>
                {account.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <InputError message={(errors as any)?.account_uid ?? undefined} />
      </div>

      <div className="grid gap-2">
        <Label htmlFor="name">Name</Label>

        <Input
          id="name"
          name="name"
          type="text"
          className="mt-1 block w-full"
          defaultValue={data.agent.name}
          autoComplete="name"
          placeholder="Name"
          onChange={(e) =>
            setData("agent", { ...data.agent, name: e.target.value })
          }
        />

        <InputError message={(errors as any)?.name ?? undefined} />
      </div>

      <div className="grid gap-2">
        <Label htmlFor="slug">Slug</Label>

        <Input
          id="slug"
          name="slug"
          type="text"
          className="mt-1 block w-full"
          defaultValue={data.agent.slug ?? ""}
          placeholder="e.g. client_intake"
          onChange={(e) =>
            setData("agent", {
              ...data.agent,
              slug: e.target.value,
            })
          }
        />

        <InputError message={(errors as any)?.slug ?? undefined} />
      </div>

      <div className="grid gap-2">
        <Label htmlFor="summary">Summary</Label>

        <Textarea
          id="summary"
          name="summary"
          className="mt-1 block w-full"
          defaultValue={data.agent.summary}
          placeholder="Write a one-line summary"
          onChange={(e) =>
            setData("agent", {
              ...data.agent,
              summary: e.target.value,
            })
          }
        />

        <InputError message={(errors as any)?.summary ?? undefined} />
      </div>

      <div className="grid gap-2">
        <Label htmlFor="problem_solved">Problem solved</Label>

        <Textarea
          id="problem_solved"
          name="problem_solved"
          className="mt-1 block w-full"
          defaultValue={data.agent.problem_solved}
          placeholder="Explain the problem this agent solves"
          onChange={(e) =>
            setData("agent", {
              ...data.agent,
              problem_solved: e.target.value,
            })
          }
        />

        <InputError message={(errors as any)?.problem_solved ?? undefined} />
      </div>

      <div className="grid gap-2">
        <Label htmlFor="description">Description</Label>

        <Textarea
          id="description"
          name="description"
          className="mt-1 block w-full"
          defaultValue={data.agent.description}
          placeholder="Explain how this agent works"
          onChange={(e) =>
            setData("agent", {
              ...data.agent,
              description: e.target.value,
            })
          }
        />

        <InputError message={(errors as any)?.description ?? undefined} />
      </div>

      <div className="grid gap-2">
        <Label htmlFor="state">Status</Label>

        <Select
          value={data.agent.state}
          onValueChange={(val) =>
            setData("agent", { ...data.agent, state: val })
          }
        >
          <SelectTrigger id="state" className="w-full">
            <SelectValue placeholder="Select a status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="provisional">Provisional</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="operational">Operational</SelectItem>
            <SelectItem value="degraded">Degraded</SelectItem>
            <SelectItem value="paused">Paused</SelectItem>
            <SelectItem value="retired">Retired</SelectItem>
          </SelectContent>
        </Select>
        <InputError message={(errors as any)?.state ?? undefined} />
      </div>
    </>
  )

  const metricsFields = (
    <>
      <div className="grid gap-2">
        <div className="flex items-center gap-2">
          <Label htmlFor="manual_task_time_in_minutes">
            Manual time per task
          </Label>
          <RecordFieldTooltip>
            Approximate time a person would spend completing this task manually,
            per run, without automation. Use a typical case, not a worst-case
            scenario.
          </RecordFieldTooltip>
        </div>
        <Input
          id="manual_task_time_in_minutes"
          name="manual_task_time_in_minutes"
          type="number"
          suffix="minutes"
          className="mt-1 block w-full"
          defaultValue={data.agent.manual_task_time_in_minutes ?? undefined}
          onChange={(e) =>
            setData("agent", {
              ...data.agent,
              manual_task_time_in_minutes: e.target.value
                ? Number(e.target.value)
                : undefined,
            })
          }
        />
        <InputError
          message={(errors as any)?.manual_task_time_in_minutes ?? undefined}
        />
      </div>

      <div className="grid gap-2">
        <div className="flex items-center gap-2">
          <Label htmlFor="hourly_rate">Hourly Rate</Label>
          <RecordFieldTooltip>
            Estimated hourly cost of the person or blended role that normally
            performs this work. A reasonable average is sufficient
          </RecordFieldTooltip>
        </div>

        <CurrencyInput
          id="hourly_rate"
          name="hourly_rate"
          className="mt-1 block w-full"
          value={data.agent.hourly_rate ?? undefined}
          onChange={(value) =>
            setData("agent", {
              ...data.agent,
              hourly_rate: value ?? undefined,
            })
          }
        />

        <InputError message={(errors as any)?.hourly_rate ?? undefined} />
      </div>

      <AgentMetricsForm
        existingMetrics={existingMetrics}
        metrics={data.agent.agent_metrics_attributes || []}
        onMetricsChange={(metrics) =>
          setData("agent", {
            ...data.agent,
            agent_metrics_attributes: metrics,
          })
        }
        errors={errors}
      />
    </>
  )

  const aiConfigFields = (
    <>
      <div className="grid gap-2">
        <Label htmlFor="model_id">Model</Label>
        <Select
          value={data.agent.model_id ?? ""}
          onValueChange={(val) =>
            setData("agent", {
              ...data.agent,
              model_id: val === "" ? undefined : val,
            })
          }
        >
          <SelectTrigger id="model_id" className="w-full">
            <SelectValue placeholder="Select a model" />
          </SelectTrigger>
          <SelectContent>
            {availableModels.map((model) => (
              <SelectItem key={model.id} value={model.id}>
                {model.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <InputError message={(errors as any)?.model_id ?? undefined} />
      </div>

      <div className="grid gap-2">
        <Label htmlFor="system_prompt">System prompt</Label>
        <MarkdownInput
          id="system_prompt"
          value={data.agent.system_prompt ?? ""}
          onChange={(value) =>
            setData("agent", {
              ...data.agent,
              system_prompt: value,
            })
          }
          placeholder="Enter the system prompt for this agent..."
        />
        <InputError message={(errors as any)?.system_prompt ?? undefined} />
      </div>

      {availableSkills.length > 0 && (
        <div className="grid gap-2">
          <Label>Skills</Label>
          <div className="space-y-2">
            {availableSkills.map((skill) => {
              const currentSkills = data.agent.skills || []
              const isChecked = currentSkills.includes(skill.type)
              return (
                <label key={skill.type} className="flex items-start gap-2">
                  <Checkbox
                    checked={isChecked}
                    onCheckedChange={(checked) => {
                      const updated = checked
                        ? [...currentSkills, skill.type]
                        : currentSkills.filter((s) => s !== skill.type)
                      setData("agent", {
                        ...data.agent,
                        skills: updated,
                      })
                    }}
                  />
                  <div>
                    <div className="text-sm font-medium">{skill.label}</div>
                    <div className="text-muted-foreground text-xs">
                      {skill.description}
                    </div>
                  </div>
                </label>
              )
            })}
          </div>
        </div>
      )}

      <div className="grid gap-2">
        <Label htmlFor="launch_title">Launch title</Label>
        <Input
          id="launch_title"
          name="launch_title"
          type="text"
          className="mt-1 block w-full"
          defaultValue={data.agent.launch_title ?? ""}
          placeholder="Title shown on the suggestion card"
          onChange={(e) =>
            setData("agent", {
              ...data.agent,
              launch_title: e.target.value,
            })
          }
        />
        <InputError message={(errors as any)?.launch_title ?? undefined} />
      </div>

      <div className="grid gap-2">
        <Label htmlFor="launch_prompt">Launch prompt</Label>
        <Textarea
          id="launch_prompt"
          name="launch_prompt"
          className="mt-1 block w-full"
          defaultValue={data.agent.launch_prompt ?? ""}
          placeholder="Prompt text shown on the suggestion card"
          onChange={(e) =>
            setData("agent", {
              ...data.agent,
              launch_prompt: e.target.value,
            })
          }
        />
        <InputError message={(errors as any)?.launch_prompt ?? undefined} />
      </div>

      <div className="grid gap-2">
        <Label htmlFor="launch_icon">Launch icon</Label>
        <Input
          id="launch_icon"
          name="launch_icon"
          type="text"
          className="mt-1 block w-full"
          defaultValue={data.agent.launch_icon ?? ""}
          placeholder="e.g. scan-line"
          onChange={(e) =>
            setData("agent", {
              ...data.agent,
              launch_icon: e.target.value,
            })
          }
        />
        <InputError message={(errors as any)?.launch_icon ?? undefined} />
      </div>
    </>
  )

  const advancedFields = (
    <>
      <AgentIntegrationsForm
        kinds={data.agent.integration_kinds || []}
        onKindsChange={(kinds) => {
          const currentResources = data.agent.resources_by_kind || {}
          const cleanedResources: Record<string, AgentResource[]> = {}
          for (const kind of kinds) {
            if (currentResources[kind]) {
              cleanedResources[kind] = currentResources[kind]
            }
          }
          setData("agent", {
            ...data.agent,
            integration_kinds: kinds,
            resources_by_kind: cleanedResources,
          })
        }}
      />

      <AgentResourcesForm
        kinds={data.agent.integration_kinds || []}
        accountUid={data.agent.account_uid}
        resourcesByKind={data.agent.resources_by_kind || {}}
        onResourcesChange={(resourcesByKind) =>
          setData("agent", {
            ...data.agent,
            resources_by_kind: resourcesByKind,
          })
        }
      />
    </>
  )

  if (mode === "new") {
    return (
      <>
        {generalFields}
        {metricsFields}
        {aiConfigFields}
        {advancedFields}
        <div className="pb-6" />
      </>
    )
  }

  return (
    <Tabs defaultValue="general" className="w-full">
      <TabsList>
        <TabsTrigger value="general">General</TabsTrigger>
        <TabsTrigger value="metrics">Metrics</TabsTrigger>
        <TabsTrigger value="ai-config">AI Config</TabsTrigger>
        <TabsTrigger value="advanced">Advanced</TabsTrigger>
        {promptVersions.length > 0 && (
          <TabsTrigger value="versions">Versions</TabsTrigger>
        )}
      </TabsList>

      <TabsContent value="general" className="space-y-6 pt-4">
        {generalFields}
      </TabsContent>

      <TabsContent value="metrics" className="space-y-6 pt-4">
        {metricsFields}
      </TabsContent>

      <TabsContent value="ai-config" className="space-y-6 pt-4">
        {aiConfigFields}
      </TabsContent>

      <TabsContent value="advanced" className="space-y-6 pt-4">
        {advancedFields}
      </TabsContent>

      {promptVersions.length > 0 && (
        <TabsContent value="versions" className="space-y-6 pt-4">
          <AgentPromptVersions
            versions={promptVersions}
            onRestore={(prompt) =>
              setData("agent", {
                ...data.agent,
                system_prompt: prompt,
              })
            }
          />
        </TabsContent>
      )}
    </Tabs>
  )
}
